<template>
  <b-form-group class="text-capitalize" :label="label" :label-for="inputId">
    <v-select
      v-model="selected"
      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
      :options="options"
      :reduce="(val) => val.value"
      :input-id="inputId"
      :disabled="disabled"
      :placeholder="$t('stream_type.select_placeholder')"
      multiple
    />

    <!-- Selected types in the order they will be returned -->
    <ul v-if="selected.length" class="stream-types-order mt-1">
      <li v-for="(id, index) in selected" :key="id" class="stream-types-order__item">
        <span class="stream-types-order__position">{{ index + 1 }}</span>
        <span class="stream-types-order__label">{{ labelOf(id) }}</span>
        <span class="stream-types-order__actions">
          <feather-icon
            icon="ChevronUpIcon"
            size="16"
            :class="index === 0 || disabled ? 'text-muted' : 'cursor-pointer'"
            @click="moveUp(index)"
          />
          <feather-icon
            icon="ChevronDownIcon"
            size="16"
            :class="index === selected.length - 1 || disabled ? 'text-muted' : 'cursor-pointer'"
            @click="moveDown(index)"
          />
          <feather-icon
            icon="XIcon"
            size="16"
            :class="disabled ? 'text-muted' : 'cursor-pointer text-danger'"
            @click="remove(index)"
          />
        </span>
      </li>
    </ul>

    <!-- Nothing selected: show what will be used instead -->
    <small v-else class="text-muted">
      <template v-if="inheritedLabels.length">
        {{ $t("stream_type.inherited_from_master_account") }}:
        {{ inheritedLabels.join(", ") }}
      </template>
      <template v-else>
        {{ emptyHint || $t("stream_type.none_configured") }}
      </template>
    </small>
  </b-form-group>
</template>

<script>
import { BFormGroup } from "bootstrap-vue";
import vSelect from "vue-select";
import { ref, computed, watch } from "@vue/composition-api";

export default {
  components: {
    BFormGroup,
    vSelect,
  },
  model: {
    prop: "value",
    event: "input",
  },
  props: {
    // Ordered array of stream type ids
    value: {
      type: Array,
      default: () => [],
    },
    // [{ value, label }]
    options: {
      type: Array,
      default: () => [],
    },
    label: {
      type: String,
      required: true,
    },
    inputId: {
      type: String,
      default: "stream_types",
    },
    // Ordered array of stream type ids used when nothing is selected here
    inheritedValue: {
      type: Array,
      default: () => [],
    },
    emptyHint: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    // Accept both the API shape ([{ id, type, order }]) and plain ids
    const toIds = (list) =>
      (list || [])
        .map((item) => (item && typeof item === "object" ? item.id : item))
        .filter((id) => id !== undefined && id !== null);

    const selected = ref(toIds(props.value));

    watch(
      () => props.value,
      (newValue) => {
        const current = JSON.stringify(selected.value);
        const incoming = JSON.stringify(toIds(newValue));
        if (current !== incoming) {
          selected.value = toIds(newValue);
        }
      }
    );

    watch(selected, (newValue) => {
      emit("input", newValue);
    });

    const labelOf = (id) => {
      const option = props.options.find((item) => item.value === id);
      return option ? option.label : id;
    };

    const inheritedLabels = computed(() =>
      toIds(props.inheritedValue).map((id) => labelOf(id))
    );

    const move = (from, to) => {
      if (props.disabled) return;
      if (to < 0 || to >= selected.value.length) return;
      const items = [...selected.value];
      const [item] = items.splice(from, 1);
      items.splice(to, 0, item);
      selected.value = items;
    };

    const moveUp = (index) => move(index, index - 1);
    const moveDown = (index) => move(index, index + 1);

    const remove = (index) => {
      if (props.disabled) return;
      const items = [...selected.value];
      items.splice(index, 1);
      selected.value = items;
    };

    return {
      selected,
      labelOf,
      inheritedLabels,
      moveUp,
      moveDown,
      remove,
    };
  },
};
</script>

<style scoped>
.stream-types-order {
  list-style: none;
  margin: 0;
  padding: 0;
}

.stream-types-order__item {
  display: flex;
  align-items: center;
  padding: 4px 8px;
  border: 1px solid rgba(128, 128, 128, 0.25);
  border-radius: 4px;
  margin-bottom: 4px;
}

.stream-types-order__position {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  margin-right: 8px;
  border-radius: 50%;
  background-color: rgba(128, 128, 128, 0.15);
  font-size: 12px;
  font-weight: 600;
}

.stream-types-order__label {
  flex: 1;
}

.stream-types-order__actions {
  display: flex;
  align-items: center;
  gap: 6px;
}
</style>
